import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { formatDateTimeEs } from "../../utils/formatters";
import { actualizarCampanasDocumentacionPersona } from "../../services/apis";

const MAX_DOCUMENT_SIZE_BYTES = 25 * 1024 * 1024;
const DOCUMENT_INPUT_ACCEPT = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt";
const DOCUMENT_INPUT_HINT = "PDF, imagenes y ficheros ofimaticos";
const ACCEPTED_DOCUMENT_EXTENSIONS = [
	".csv",
	".doc",
	".docx",
	".jpeg",
	".jpg",
	".pdf",
	".png",
	".txt",
	".xls",
	".xlsx",
];

const defaultLoadDocuments = async (persona) => ({
	success: true,
	data: {
		carpetas: [],
		documentos: [],
	},
	message: `No hay documentos configurados para la persona ${persona?.id ?? "-"}.`,
});

const toArray = (value) => (Array.isArray(value) ? value : []);

const normalizeDocumentsResponse = (response) => ({
	success: response?.success !== false,
	carpetas: toArray(response?.data?.carpetas ?? response?.carpetas),
	documentos: toArray(response?.data?.documentos ?? response?.documentos ?? response?.data),
	campanasDisponibles: toArray(response?.data?.campanas_disponibles),
	campanasAsignadasIds: toArray(response?.data?.campanas_asignadas_ids)
		.map((id) => Number(id))
		.filter((id) => id > 0),
	canManageCampaigns: response?.data?.can_manage_campaigns === true,
	message:
		response?.data?.message ||
		response?.message ||
		"No hay documentos disponibles para esta persona.",
});

const getDocumentName = (documento) =>
	documento?.nombre ||
	documento?.titulo ||
	documento?.archivo_nombre ||
	documento?.filename ||
	documento?.name ||
	"Documento sin nombre";

const getDocumentType = (documento) =>
	documento?.tipo || documento?.categoria || documento?.tipo_documento || null;

const getDocumentPrivacy = (documento) =>
	documento?.privacidad || documento?.visibilidad || null;

const getDocumentStatus = (documento) =>
	documento?.estado || documento?.status || documento?.situacion || null;

const getDocumentDate = (documento) =>
	documento?.fecha_creacion ||
	documento?.fecha_subida ||
	documento?.created_at ||
	documento?.updated_at ||
	null;

const getFolderName = (folder) => folder?.nombre || "Carpeta sin nombre";
const getFolderCampaignName = (folder) => folder?.campana_nombre || "Sin campana";

const normalizeStatus = (value) => String(value || "").trim().toLowerCase();

const EXPIRY_WARNING_DAYS = 15;

const hasExpiredOrPendingStatus = (value) =>
	[
		"pendiente",
		"revision",
		"en revision",
		"en_revision",
		"caducado",
		"rechazado",
		"error",
		"inactivo",
	].includes(normalizeStatus(value));

const getStatusColor = (value) => {
	const normalized = normalizeStatus(value);

	if (["activo", "vigente", "ok", "firmado", "completado", "subido"].includes(normalized)) {
		return "success";
	}

	if (hasExpiredOrPendingStatus(normalized)) {
		return "error";
	}

	return "default";
};

const getExpiryColor = (value) => {
	const normalized = String(value || "").trim().slice(0, 10);

	if (!normalized) {
		return "default";
	}

	if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
		return "default";
	}

	const [year, month, day] = normalized.split("-").map(Number);
	const expiryUtc = Date.UTC(year, month - 1, day);
	const today = new Date();
	const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
	const diffDays = Math.floor((expiryUtc - todayUtc) / (1000 * 60 * 60 * 24));

	if (diffDays < 0) {
		return "error";
	}

	if (diffDays <= EXPIRY_WARNING_DAYS) {
		return "warning";
	}

	return "success";
};

const getDocumentVisualState = (documento) => {
	const normalizedStatus = normalizeStatus(getDocumentStatus(documento));

	if (hasExpiredOrPendingStatus(normalizedStatus)) {
		return "error";
	}

	const expiryColor = getExpiryColor(documento?.fecha_caducidad);

	return expiryColor === "default" ? "success" : expiryColor;
};

const getFolderVisualState = (documents) => {
	if (!Array.isArray(documents) || documents.length === 0) {
		return {
			color: "error",
			label: "Pendiente",
			subtitle: "Pendiente de subir",
			hasCurrentDocument: false,
		};
	}

	const states = documents.map(getDocumentVisualState);

	if (states.includes("success")) {
		return {
			color: "success",
			label: "Vigente",
			subtitle: "Documento vigente disponible",
			hasCurrentDocument: true,
		};
	}

	if (states.includes("warning")) {
		return {
			color: "warning",
			label: "Caduca pronto",
			subtitle: "Documento vigente con caducidad proxima",
			hasCurrentDocument: true,
		};
	}

	return {
		color: "error",
		label: "Caducado",
		subtitle: "Los documentos existentes ya no estan vigentes",
		hasCurrentDocument: false,
	};
};

const getFileExtension = (fileName = "") => {
	const normalizedName = String(fileName || "").trim().toLowerCase();
	const lastDot = normalizedName.lastIndexOf(".");

	if (lastDot <= -1) {
		return "";
	}

	return normalizedName.slice(lastDot);
};

const getFileSignature = (file) =>
	[file?.name || "", file?.size || 0, file?.lastModified || 0].join("-");

const isAcceptedDocument = (file) => {
	if (!file) return false;

	if (String(file.type || "").startsWith("image/")) {
		return true;
	}

	return ACCEPTED_DOCUMENT_EXTENSIONS.includes(getFileExtension(file.name));
};

const formatFileSize = (bytes) => {
	const size = Number(bytes || 0);

	if (size <= 0) return "0 KB";
	if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;

	return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const buildPendingDocument = (file) => {
	const extension = getFileExtension(file?.name).replace(".", "").toUpperCase();
	const fallbackType = String(file?.type || "").startsWith("image/")
		? "Imagen"
		: extension || "Archivo";

	return {
		id: `${getFileSignature(file)}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
		file,
		signature: getFileSignature(file),
		name: file?.name || "Documento sin nombre",
		sizeLabel: formatFileSize(file?.size),
		typeLabel: fallbackType,
	};
};

export const PersonaDocumentosPanel = ({
	persona,
	expanded,
	folderFilterIds = [],
	loadDocuments = defaultLoadDocuments,
	allowDropzone = false,
	uploadDocuments,
	downloadDocument,
	deleteDocument,
}) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [infoMessage, setInfoMessage] = useState("");
	const [carpetas, setCarpetas] = useState([]);
	const [documentos, setDocumentos] = useState([]);
	const [campanasDisponibles, setCampanasDisponibles] = useState([]);
	const [campanasAsignadasIds, setCampanasAsignadasIds] = useState([]);
	const [canManageCampaigns, setCanManageCampaigns] = useState(false);
	const [savingCampaigns, setSavingCampaigns] = useState(false);
	const [expandedFolderId, setExpandedFolderId] = useState(null);
	const [showUploadFormFolderId, setShowUploadFormFolderId] = useState(null);
	const [selectedExpiryDate, setSelectedExpiryDate] = useState("");
	const [dragActive, setDragActive] = useState(false);
	const [pendingFiles, setPendingFiles] = useState([]);
	const [queueMessage, setQueueMessage] = useState("");
	const [queueMessageSeverity, setQueueMessageSeverity] = useState("info");
	const [queueError, setQueueError] = useState("");
	const [uploading, setUploading] = useState(false);
	const [downloadingDocumentId, setDownloadingDocumentId] = useState(null);
	const [deletingDocumentId, setDeletingDocumentId] = useState(null);
	const [documentToDelete, setDocumentToDelete] = useState(null);
	const [activePickerFolderId, setActivePickerFolderId] = useState(null);
	const fileInputRef = useRef(null);
	const pendingFilesRef = useRef([]);
	const wasExpandedRef = useRef(false);
	const selectedFolderIds = useMemo(
		() =>
			new Set(
				(Array.isArray(folderFilterIds) ? folderFilterIds : [])
					.map((folderId) => Number(folderId))
					.filter((folderId) => folderId > 0)
			),
		[folderFilterIds]
	);

	useEffect(() => {
		pendingFilesRef.current = pendingFiles;
	}, [pendingFiles]);

	useEffect(() => {
		setLoading(false);
		setError("");
		setInfoMessage("");
		setCarpetas([]);
		setDocumentos([]);
		setCampanasDisponibles([]);
		setCampanasAsignadasIds([]);
		setCanManageCampaigns(false);
		setSavingCampaigns(false);
		setExpandedFolderId(null);
		setShowUploadFormFolderId(null);
		setSelectedExpiryDate("");
		setDragActive(false);
		setPendingFiles([]);
		setQueueMessage("");
		setQueueMessageSeverity("info");
		setQueueError("");
		setUploading(false);
		setDownloadingDocumentId(null);
		setDeletingDocumentId(null);
		setDocumentToDelete(null);
		setActivePickerFolderId(null);
		wasExpandedRef.current = false;
	}, [persona?.id]);

	const cargarDocumentos = useCallback(async () => {
		setLoading(true);
		setError("");

		try {
			const response = await loadDocuments(persona);
			const normalized = normalizeDocumentsResponse(response);
			const visibleCarpetas =
				selectedFolderIds.size > 0
					? normalized.carpetas.filter((folder) =>
							selectedFolderIds.has(Number(folder?.id || 0))
					  )
					: normalized.carpetas;
			const visibleDocumentos =
				selectedFolderIds.size > 0
					? normalized.documentos.filter((documento) =>
							selectedFolderIds.has(
								Number(documento?.id_carpeta_documentacion || 0)
							)
					  )
					: normalized.documentos;

			setCarpetas(visibleCarpetas);
			setDocumentos(visibleDocumentos);
			setCampanasDisponibles(normalized.campanasDisponibles);
			setCampanasAsignadasIds(normalized.campanasAsignadasIds);
			setCanManageCampaigns(normalized.canManageCampaigns);
			setInfoMessage(normalized.message);

			if (normalized.success === false) {
				setError(normalized.message || "No se pudieron cargar los documentos");
			}
		} catch (loadError) {
			console.error("Error cargando documentos de persona:", loadError);
			setCarpetas([]);
			setDocumentos([]);
			setCampanasDisponibles([]);
			setCampanasAsignadasIds([]);
			setCanManageCampaigns(false);
			setError("No se pudieron cargar los documentos");
		} finally {
			setLoading(false);
		}
	}, [loadDocuments, persona, selectedFolderIds]);

	useEffect(() => {
		if (expanded && !wasExpandedRef.current) {
			void cargarDocumentos();
		}

		wasExpandedRef.current = expanded;
	}, [cargarDocumentos, expanded]);

	const selectedCampaigns = useMemo(
		() =>
			campanasDisponibles.filter((campana) =>
				campanasAsignadasIds.includes(Number(campana?.id || 0))
			),
		[campanasDisponibles, campanasAsignadasIds]
	);

	const handleCampaignsChange = async (_, selectedCampaignsValue) => {
		const id_campanas = [
			...new Set(
				selectedCampaignsValue
					.map((campana) => Number(campana?.id || 0))
					.filter((id) => id > 0)
			),
		];

		try {
			setSavingCampaigns(true);
			setError("");
			const response = await actualizarCampanasDocumentacionPersona({
				id_usuario: persona?.id,
				id_campanas,
			});

			if (response?.success !== true) {
				setError(response?.message || "No se pudieron actualizar las campanas documentales.");
				return;
			}

			await cargarDocumentos();
		} catch (saveError) {
			console.error("Error actualizando campanas documentales:", saveError);
			setError("No se pudieron actualizar las campanas documentales.");
		} finally {
			setSavingCampaigns(false);
		}
	};

	useEffect(() => {
		if (carpetas.length === 0) {
			if (expandedFolderId !== null) {
				setExpandedFolderId(null);
			}
			return;
		}

		if (expandedFolderId === null) {
			return;
		}

		const folderExists = carpetas.some((folder) => Number(folder?.id) === Number(expandedFolderId));

		if (!folderExists) {
			setExpandedFolderId(carpetas[0]?.id ?? null);
		}
	}, [carpetas, expandedFolderId]);

	const getFolderDocuments = useCallback(
		(folderId) =>
			documentos.filter(
				(documento) => Number(documento?.id_carpeta_documentacion || 0) === Number(folderId || 0)
			),
		[documentos]
	);

	const campaignGroups = useMemo(() => {
		const groups = new Map();

		carpetas.forEach((folder) => {
			const campaignId = Number(folder?.id_campana || 0);
			const campaignName = getFolderCampaignName(folder);
			const groupKey = `${campaignId}-${campaignName}`;

			if (!groups.has(groupKey)) {
				groups.set(groupKey, {
					key: groupKey,
					id_campana: campaignId,
					nombre: campaignName,
					slug: String(folder?.campana_slug || "").trim(),
					activa: folder?.campana_activa !== false,
					eliminada: folder?.campana_eliminada === true,
					folders: [],
				});
			}

			groups.get(groupKey).folders.push(folder);
		});

		return Array.from(groups.values())
			.map((group) => ({
				...group,
				folders: [...group.folders].sort((left, right) =>
					String(left?.nombre || "").localeCompare(String(right?.nombre || ""), "es")
				),
			}))
			.sort((left, right) => {
				if (left.activa !== right.activa) {
					return left.activa ? -1 : 1;
				}

				return left.nombre.localeCompare(right.nombre, "es");
			});
	}, [carpetas]);

	const activeFolder =
		carpetas.find((folder) => Number(folder?.id) === Number(expandedFolderId)) || null;
	const canUploadInActiveFolder =
		Boolean(activeFolder) &&
		Number(activeFolder?.id) > 0 &&
		activeFolder?.eliminada !== true &&
		activeFolder?.historica !== true;

	const resetUploadState = useCallback(() => {
		setShowUploadFormFolderId(null);
		setSelectedExpiryDate("");
		setDragActive(false);
		setPendingFiles([]);
		setQueueMessage("");
		setQueueMessageSeverity("info");
		setQueueError("");
		setActivePickerFolderId(null);
	}, []);

	const toggleFolder = (folderId) => {
		setExpandedFolderId((current) => {
			if (Number(current) === Number(folderId)) {
				resetUploadState();
				return null;
			}

			resetUploadState();
			return folderId;
		});
	};

	const openUploadForm = useCallback((folderId) => {
		if (Number(expandedFolderId) !== Number(folderId)) {
			return;
		}

		setQueueMessage("");
		setQueueMessageSeverity("info");
		setQueueError("");
		setPendingFiles([]);
		setSelectedExpiryDate("");
		setShowUploadFormFolderId(folderId);
	}, [expandedFolderId]);

	const closeUploadForm = useCallback(() => {
		resetUploadState();
	}, [resetUploadState]);

	const openFilePicker = useCallback(
		(folderId) => {
			if (uploading || !canUploadInActiveFolder || Number(expandedFolderId) !== Number(folderId)) {
				return;
			}

			setActivePickerFolderId(folderId);
			fileInputRef.current?.click();
		},
		[canUploadInActiveFolder, expandedFolderId, uploading]
	);

	const addFilesToQueue = useCallback(
		(fileList, folderId) => {
			if (Number(expandedFolderId) !== Number(folderId)) {
				return;
			}

			const files = Array.from(fileList || []);
			if (files.length === 0) return;

			const nextQueue = [...pendingFilesRef.current];
			const usedSignatures = new Set(nextQueue.map((item) => item.signature));
			const rejected = [];
			let addedCount = 0;

			files.forEach((file) => {
				if (!(file instanceof File)) {
					return;
				}

				if (!isAcceptedDocument(file)) {
					rejected.push(`${file.name}: formato no permitido.`);
					return;
				}

				if ((file.size || 0) <= 0) {
					rejected.push(`${file.name}: archivo vacio.`);
					return;
				}

				if ((file.size || 0) > MAX_DOCUMENT_SIZE_BYTES) {
					rejected.push(`${file.name}: supera 25 MB.`);
					return;
				}

				const signature = getFileSignature(file);
				if (usedSignatures.has(signature)) {
					rejected.push(`${file.name}: ya esta en la cola.`);
					return;
				}

				usedSignatures.add(signature);
				nextQueue.push(buildPendingDocument(file));
				addedCount += 1;
			});

			if (addedCount > 0) {
				setPendingFiles(nextQueue);
				setQueueMessageSeverity("info");
				setQueueMessage(
					uploadDocuments
						? `${addedCount} documento(s) anadido(s). Puedes subirlos cuando quieras.`
						: `${addedCount} documento(s) anadido(s) a la cola local. La subida quedara lista al conectar el endpoint.`
				);
			}

			setQueueError(rejected.join(" "));
		},
		[expandedFolderId, uploadDocuments]
	);

	const handleInputChange = useCallback(
		(event) => {
			addFilesToQueue(event.target.files, activePickerFolderId);
			event.target.value = "";
		},
		[activePickerFolderId, addFilesToQueue]
	);

	const removePendingFile = useCallback((id) => {
		setPendingFiles((current) => current.filter((item) => item.id !== id));
	}, []);

	const clearPendingFiles = useCallback(() => {
		setPendingFiles([]);
		setQueueMessage("");
		setQueueMessageSeverity("info");
		setQueueError("");
	}, []);

	const handleUploadPending = useCallback(async () => {
		if (typeof uploadDocuments !== "function" || pendingFilesRef.current.length === 0) {
			return;
		}

		if (!activeFolder || !canUploadInActiveFolder) {
			setQueueError("Selecciona una carpeta valida antes de subir documentos.");
			return;
		}

		if (activeFolder?.requiere_fecha_caducidad && !selectedExpiryDate) {
			setQueueError(
				"La fecha de expiracion es obligatoria para la carpeta seleccionada."
			);
			return;
		}

		setUploading(true);
		setQueueError("");
		setQueueMessage("");
		setQueueMessageSeverity("info");

		try {
			const files = pendingFilesRef.current.map((item) => item.file);
			const response = await uploadDocuments(persona, files, {
				id_carpeta_documentacion: Number(activeFolder.id),
				fecha_caducidad: selectedExpiryDate || undefined,
			});

			if (response?.success !== true) {
				setQueueError(
					response?.message || "El servidor no devolvio una respuesta valida al subir los documentos."
				);
				return;
			}

			setPendingFiles([]);
			setQueueMessageSeverity("success");
			setQueueMessage(
				response?.message || `${files.length} documento(s) subido(s) correctamente.`
			);
			setShowUploadFormFolderId(null);

			await cargarDocumentos();
		} catch (uploadError) {
			console.error("Error subiendo documentos de persona:", uploadError);
			setQueueError(
				uploadError?.response?.data?.message || "No se pudieron subir los documentos."
			);
		} finally {
			setUploading(false);
		}
	}, [
		activeFolder,
		canUploadInActiveFolder,
		cargarDocumentos,
		persona,
		selectedExpiryDate,
		uploadDocuments,
	]);

	const handleDownloadDocument = useCallback(
		async (documento) => {
			if (typeof downloadDocument !== "function" || !documento?.id || !documento?.descargable) {
				return;
			}

			setQueueError("");
			setDownloadingDocumentId(documento.id);

			try {
				await downloadDocument(documento);
			} catch (downloadError) {
				console.error("Error descargando documento de persona:", downloadError);
				setQueueError("No se pudo descargar el documento.");
			} finally {
				setDownloadingDocumentId(null);
			}
		},
		[downloadDocument]
	);

	const openDeleteDialog = useCallback((documento) => {
		if (typeof deleteDocument !== "function" || !documento?.id) {
			return;
		}

		setDocumentToDelete(documento);
	}, [deleteDocument]);

	const closeDeleteDialog = useCallback(() => {
		if (deletingDocumentId !== null) {
			return;
		}

		setDocumentToDelete(null);
	}, [deletingDocumentId]);

	const handleDeleteDocument = useCallback(
		async () => {
			if (typeof deleteDocument !== "function" || !documentToDelete?.id) {
				return;
			}

			setQueueError("");
			setQueueMessage("");
			setQueueMessageSeverity("info");
			setDeletingDocumentId(documentToDelete.id);

			try {
				const response = await deleteDocument(documentToDelete);

				if (response?.success !== true) {
					setQueueError(response?.message || "No se pudo eliminar el documento.");
					return;
				}

				setQueueMessageSeverity("success");
				setQueueMessage(response?.message || "Documento eliminado correctamente.");
				setDocumentToDelete(null);
				await cargarDocumentos();
			} catch (deleteError) {
				console.error("Error eliminando documento de persona:", deleteError);
				setQueueError(
					deleteError?.response?.data?.message ||
						deleteError?.message ||
						"No se pudo eliminar el documento."
				);
			} finally {
				setDeletingDocumentId(null);
			}
		},
		[cargarDocumentos, deleteDocument, documentToDelete]
	);

	if (!expanded) {
		return null;
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
			<input
				ref={fileInputRef}
				type="file"
				multiple
				accept={DOCUMENT_INPUT_ACCEPT}
				onChange={handleInputChange}
				style={{ display: "none" }}
			/>

			{loading && (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1.5 }}>
					<CircularProgress size={18} />
					<Typography variant="body2">Cargando documentos...</Typography>
				</Box>
			)}

			{!loading && error && (
				<Box sx={{ py: 0.5 }}>
					<Alert severity="error">{error}</Alert>
					<Button size="small" onClick={cargarDocumentos} sx={{ mt: 1 }}>
						Reintentar
					</Button>
				</Box>
			)}

			{!loading && !error && canManageCampaigns && (
				<Paper variant="outlined" sx={{ p: 1.5 }}>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						Campanas documentales
					</Typography>
					<Autocomplete
						multiple
						disableCloseOnSelect
						disabled={savingCampaigns}
						options={campanasDisponibles.filter((campana) => campana?.activa !== false)}
						value={selectedCampaigns}
						isOptionEqualToValue={(option, selected) => Number(option?.id) === Number(selected?.id)}
						getOptionLabel={(option) => option?.nombre || `Campana ${option?.id ?? ""}`}
						onChange={handleCampaignsChange}
						renderOption={(props, option, { selected }) => (
							<li {...props} key={`persona-campana-${option.id}`}>
								<Checkbox checked={selected} sx={{ mr: 1 }} />
								{option.nombre}
							</li>
						)}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Campanas asignadas"
								placeholder={selectedCampaigns.length === 0 ? "Selecciona campanas..." : ""}
								helperText="Al cambiar la selección, se actualizan las carpetas disponibles para esta persona."
							/>
						)}
					/>
				</Paper>
			)}

			{!loading && !error && carpetas.length === 0 && (
				<Alert severity="info" sx={{ mt: 0.5 }}>
					{infoMessage || "No hay carpetas configuradas para esta persona."}
				</Alert>
			)}

			{!loading && !error && carpetas.length > 0 && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
					{campaignGroups.map((campaignGroup) => (
						<Box key={`campaign-group-${campaignGroup.key}`} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
							<Box
								sx={{
									display: "flex",
									flexDirection: { xs: "column", md: "row" },
									alignItems: { xs: "flex-start", md: "center" },
									justifyContent: "space-between",
									gap: 1,
									px: 0.5,
									pt: 0.75,
								}}
							>
								<Box>
									<Typography variant="subtitle2">{campaignGroup.nombre}</Typography>
									<Typography variant="caption" color="text.secondary">
										{campaignGroup.folders.length} carpeta(s) configurada(s) para esta campana
									</Typography>
								</Box>

								<Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
									<Chip
										size="small"
										color={campaignGroup.activa ? "success" : "default"}
										label={campaignGroup.activa ? "Campana activa" : "Campana inactiva"}
									/>
									{campaignGroup.slug && (
										<Chip size="small" variant="outlined" label={campaignGroup.slug} />
									)}
								</Box>
							</Box>

							{campaignGroup.folders.map((folder) => {
								const folderDocuments = getFolderDocuments(folder.id);
								const folderVisualState = getFolderVisualState(folderDocuments);
								const hasCurrentDocument = folderVisualState.hasCurrentDocument;
								const isExpanded = Number(expandedFolderId) === Number(folder.id);
								const isUploadFormVisible =
									isExpanded && Number(showUploadFormFolderId) === Number(folder.id);
								const isUploadable =
									allowDropzone &&
									Number(folder?.id) > 0 &&
									folder?.eliminada !== true &&
									folder?.historica !== true;

								return (
									<Paper
										variant="outlined"
										key={`folder-persona-${folder.id}`}
										sx={{ overflow: "hidden" }}
									>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												gap: 1,
												p: 1.5,
												bgcolor: isExpanded ? "grey.50" : "common.white",
											}}
										>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
												<IconButton size="small" onClick={() => toggleFolder(folder.id)}>
													{isExpanded ? (
														<ExpandMoreIcon fontSize="small" />
													) : (
														<ChevronRightIcon fontSize="small" />
													)}
												</IconButton>
												<FolderOpenOutlinedIcon color="action" fontSize="small" />
												<Box sx={{ minWidth: 0 }}>
													<Typography variant="subtitle2">{getFolderName(folder)}</Typography>
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{ display: "block" }}
													>
														{folderVisualState.subtitle}
													</Typography>
												</Box>
											</Box>

											<Box
												sx={{
													display: "flex",
													flexWrap: "wrap",
													gap: 0.75,
													justifyContent: "flex-end",
												}}
											>
												<Chip
													size="small"
													color={folderVisualState.color}
													label={folderVisualState.label}
												/>
												<Chip
													size="small"
													variant="outlined"
													label={
														folder?.requiere_fecha_caducidad
															? "Caducidad obligatoria"
															: "Caducidad opcional"
													}
												/>
												<Chip
													size="small"
													variant="outlined"
													label={`${folderDocuments.length} archivo(s)`}
												/>
											</Box>
										</Box>

										<Collapse in={isExpanded}>
											<Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
										{folder?.descripcion && (
											<Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
												{folder.descripcion}
											</Typography>
										)}

										{isExpanded && queueMessage && <Alert severity={queueMessageSeverity} sx={{ mb: 1.25 }}>{queueMessage}</Alert>}
										{isExpanded && queueError && <Alert severity="warning" sx={{ mb: 1.25 }}>{queueError}</Alert>}

										{isUploadable && (
											<Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 1.5 }}>
												{!isUploadFormVisible && (
													<Box sx={{ display: "flex", justifyContent: "flex-start" }}>
														<Button
															size="small"
															variant="contained"
															onClick={() => openUploadForm(folder.id)}
														>
															Añadir documento
														</Button>
													</Box>
												)}

												{isUploadFormVisible && (
													<>
														<Box
															sx={{
																display: "flex",
																flexDirection: { xs: "column", sm: "row" },
																alignItems: { xs: "flex-start", sm: "center" },
																justifyContent: "space-between",
																gap: 1,
															}}
														>
															<Typography variant="subtitle2">
																Nuevo documento en {getFolderName(folder)}
															</Typography>
															<Button
																size="small"
																variant="text"
																color="inherit"
																onClick={closeUploadForm}
																disabled={uploading}
															>
																Cancelar
															</Button>
														</Box>

														<TextField
															type="date"
															size="small"
															fullWidth
															label="Fecha de expiracion"
															value={selectedExpiryDate}
															onChange={(event) => setSelectedExpiryDate(event.target.value)}
															required={Boolean(folder?.requiere_fecha_caducidad)}
															InputLabelProps={{ shrink: true }}
															helperText={
																folder?.requiere_fecha_caducidad
																	? "Obligatoria para esta carpeta"
																	: "Opcional para esta carpeta"
															}
														/>

														<Paper
															variant="outlined"
															role="button"
															tabIndex={0}
															onClick={() => openFilePicker(folder.id)}
															onKeyDown={(event) => {
																if (event.key === "Enter" || event.key === " ") {
																	event.preventDefault();
																	openFilePicker(folder.id);
																}
															}}
															onDragEnter={(event) => {
																event.preventDefault();
																if (canUploadInActiveFolder && isExpanded) {
																	setDragActive(true);
																}
															}}
															onDragOver={(event) => {
																event.preventDefault();
																if (canUploadInActiveFolder && isExpanded) {
																	setDragActive(true);
																}
															}}
															onDragLeave={(event) => {
																event.preventDefault();
																if (event.currentTarget === event.target) {
																	setDragActive(false);
																}
															}}
															onDrop={(event) => {
																event.preventDefault();
																setDragActive(false);
																if (!canUploadInActiveFolder || !isExpanded) {
																	return;
																}
																addFilesToQueue(event.dataTransfer?.files, folder.id);
															}}
															sx={{
																p: 2,
																borderStyle: "dashed",
																borderWidth: 2,
																borderColor: dragActive ? "primary.main" : "divider",
																bgcolor: dragActive ? "primary.50" : "grey.50",
																cursor: "pointer",
																transition: "all 0.2s ease",
															}}
														>
															<Box
																sx={{
																	display: "flex",
																	flexDirection: { xs: "column", sm: "row" },
																	alignItems: { xs: "flex-start", sm: "center" },
																	justifyContent: "space-between",
																	gap: 1.5,
																}}
															>
																<Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
																	<CloudUploadOutlinedIcon color={dragActive ? "primary" : "action"} />
																	<Box>
																		<Typography variant="subtitle2">
																			Arrastra documentos aqui o haz click para seleccionarlos
																		</Typography>
																		<Typography variant="caption" color="text.secondary">
																			Admite {DOCUMENT_INPUT_HINT}. Maximo 25 MB por archivo.
																		</Typography>
																	</Box>
																</Box>

																<Button
																	size="small"
																	variant="outlined"
																	onClick={(event) => {
																		event.stopPropagation();
																		openFilePicker(folder.id);
																	}}
																	disabled={uploading}
																>
																	Seleccionar archivos
																</Button>
															</Box>
														</Paper>
													</>
												)}
											</Box>
										)}

										{folder?.historica && (
											<Alert severity="warning" sx={{ mb: 1.25 }}>
												Esta carpeta es historica y solo permite consulta.
											</Alert>
										)}

										{pendingFiles.length > 0 && isExpanded && isUploadFormVisible && (
											<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5 }}>
												<Box
													sx={{
														display: "flex",
														flexDirection: { xs: "column", sm: "row" },
														alignItems: { xs: "flex-start", sm: "center" },
														justifyContent: "space-between",
														gap: 1,
													}}
												>
													<Box>
														<Typography variant="subtitle2">
															Documentos en cola ({pendingFiles.length})
														</Typography>
														<Typography variant="caption" color="text.secondary">
															Se subiran a la carpeta {getFolderName(folder)}
															{selectedExpiryDate
																? ` con caducidad ${formatDateTimeEs(selectedExpiryDate, {
																		includeTime: false,
																  })}`
																: ""}
														</Typography>
													</Box>

													<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
														{typeof uploadDocuments === "function" && isUploadable && (
															<Button
																size="small"
																variant="contained"
																onClick={handleUploadPending}
																disabled={uploading}
															>
																{uploading ? "Subiendo..." : "Subir documentos"}
															</Button>
														)}
														<Button
															size="small"
															variant="text"
															color="inherit"
															onClick={clearPendingFiles}
															disabled={uploading}
														>
															Vaciar cola
														</Button>
													</Box>
												</Box>

												<Grid container spacing={1.25}>
													{pendingFiles.map((item) => (
														<Grid item size={{ xs: 12, md: 6 }} key={item.id}>
															<Paper
																variant="outlined"
																sx={{
																	p: 1.5,
																	borderStyle: "dashed",
																}}
															>
																<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
																	<InsertDriveFileIcon color="action" fontSize="small" sx={{ mt: 0.2 }} />
																	<Box sx={{ minWidth: 0, flex: 1 }}>
																		<Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
																			{item.name}
																		</Typography>

																		<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
																			<Chip size="small" label={item.typeLabel} />
																			<Chip size="small" variant="outlined" label={item.sizeLabel} />
																			{selectedExpiryDate && (
																				<Chip
																					size="small"
																					color={getExpiryColor(selectedExpiryDate)}
																					label={`Caduca ${formatDateTimeEs(selectedExpiryDate, {
																						includeTime: false,
																					})}`}
																				/>
																			)}
																			<Chip size="small" color="error" label="Pendiente" />
																		</Box>
																	</Box>

																	<IconButton
																		size="small"
																		onClick={() => removePendingFile(item.id)}
																		disabled={uploading}
																	>
																		<CloseIcon fontSize="small" />
																	</IconButton>
																</Box>
															</Paper>
														</Grid>
													))}
												</Grid>
											</Box>
										)}

										{!hasCurrentDocument && (
											<Alert severity={folderDocuments.length > 0 ? "warning" : "info"} sx={{ mb: folderDocuments.length > 0 ? 1.25 : 0 }}>
												{folderDocuments.length > 0
													? "Pendiente: no hay ningun documento vigente en esta carpeta."
													: "Pendiente: todavia no se ha subido ningun documento para esta carpeta."}
											</Alert>
										)}

										{folderDocuments.length > 0 && (
											<Grid container spacing={1.25}>
												{folderDocuments.map((documento, index) => {
													const nombre = getDocumentName(documento);
													const tipo = getDocumentType(documento);
													const privacidad = getDocumentPrivacy(documento);
													const estado = getDocumentStatus(documento);
													const fecha = getDocumentDate(documento);
													const fechaCaducidad = documento?.fecha_caducidad || null;
													const tamano = Number(documento?.tamano_bytes || 0);
													const isDownloading = downloadingDocumentId === documento?.id;
													const isDeleting = deletingDocumentId === documento?.id;

													return (
														<Grid
															item
															size={{ xs: 12, md: 6 }}
															key={`${persona?.id ?? "persona"}-${folder.id}-${documento?.id ?? nombre}-${index}`}
														>
															<Paper variant="outlined" sx={{ p: 1.5 }}>
																<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
																	<InsertDriveFileIcon color="action" fontSize="small" sx={{ mt: 0.2 }} />
																	<Box sx={{ minWidth: 0, flex: 1 }}>
																		<Typography variant="body2" sx={{ fontWeight: 600 }}>
																			{nombre}
																		</Typography>

																		<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
																			{tipo && <Chip size="small" label={tipo} />}
																			{privacidad && (
																				<Chip size="small" variant="outlined" label={privacidad} />
																			)}
																			{tamano > 0 && (
																				<Chip
																					size="small"
																					variant="outlined"
																					label={formatFileSize(tamano)}
																				/>
																			)}
																			{estado && (
																				<Chip
																					size="small"
																					color={getStatusColor(estado)}
																					label={estado}
																				/>
																			)}
																			{fechaCaducidad && (
																				<Chip
																					size="small"
																					color={getExpiryColor(fechaCaducidad)}
																					label={`Caduca ${formatDateTimeEs(fechaCaducidad, {
																						includeTime: false,
																					})}`}
																				/>
																			)}
																		</Box>

																		{fecha && (
																			<Typography
																				variant="caption"
																				color="text.secondary"
																				sx={{ mt: 1, display: "block" }}
																			>
																				Subido el {formatDateTimeEs(fecha)}
																			</Typography>
																		)}

																		<Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1 }}>
																			{typeof downloadDocument === "function" && documento?.descargable && (
																				<Button
																					size="small"
																					variant="text"
																					startIcon={
																						isDownloading ? (
																							<CircularProgress size={14} />
																						) : (
																							<DownloadOutlinedIcon fontSize="small" />
																						)
																					}
																					onClick={() => handleDownloadDocument(documento)}
																					disabled={isDownloading || isDeleting}
																					sx={{ px: 0, justifyContent: "flex-start" }}
																				>
																					{isDownloading ? "Descargando..." : "Descargar"}
																				</Button>
																			)}
																		</Box>
																	</Box>

																	{typeof deleteDocument === "function" && documento?.id && (
																		<IconButton
																			size="small"
																			color="error"
																			onClick={() => openDeleteDialog(documento)}
																			disabled={isDeleting || isDownloading}
																			title={isDeleting ? "Eliminando..." : "Eliminar documento"}
																			sx={{ mt: -0.5, mr: -0.5 }}
																		>
																			{isDeleting ? (
																				<CircularProgress size={16} color="inherit" />
																			) : (
																				<DeleteOutlineIcon fontSize="small" />
																			)}
																		</IconButton>
																	)}
																</Box>
															</Paper>
														</Grid>
													);
												})}
											</Grid>
										)}
									</Box>
								</Collapse>
							</Paper>
								);
							})}
						</Box>
					))}

					{infoMessage && (
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, opacity: 0.75 }}>
							<DescriptionOutlinedIcon fontSize="small" color="action" />
							<Typography variant="caption">{infoMessage}</Typography>
						</Box>
					)}
				</Box>
			)}

			<Dialog
				open={Boolean(documentToDelete)}
				onClose={closeDeleteDialog}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle sx={{ pb: 1 }}>Eliminar documento</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ mb: 1.5 }}>
						Vas a eliminar este documento de la persona.
					</Typography>
					<Paper
						variant="outlined"
						sx={{
							p: 1.5,
							borderRadius: 2,
							bgcolor: "grey.50",
						}}
					>
						<Typography variant="body2" sx={{ fontWeight: 700 }}>
							{getDocumentName(documentToDelete)}
						</Typography>
						{documentToDelete?.archivo_nombre && (
							<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
								{documentToDelete.archivo_nombre}
							</Typography>
						)}
					</Paper>
					<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
						Esta accion no se puede deshacer.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={closeDeleteDialog} disabled={deletingDocumentId !== null} color="inherit">
						Cancelar
					</Button>
					<Button
						onClick={handleDeleteDocument}
						disabled={deletingDocumentId !== null}
						color="error"
						variant="contained"
						startIcon={
							deletingDocumentId !== null ? (
								<CircularProgress size={14} color="inherit" />
							) : (
								<DeleteOutlineIcon fontSize="small" />
							)
						}
					>
						{deletingDocumentId !== null ? "Eliminando..." : "Eliminar"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
